import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { FaSearch, FaTrash, FaRobot, FaCheckCircle, FaTimesCircle, FaHourglassHalf } from 'react-icons/fa';
import KpiCard from '../components/KpiCard/KpiCard.jsx';
import DailyChart from '../components/DailyChart/DailyChart.jsx';
import DataTable from '../components/DataTable/DataTable.jsx';
import Pagination from '../components/Pagination/Pagination.jsx';
import automacoesApi from '../services/automacoesApi.js';
import { colors } from '../utils/colors.js';

const Title = styled.h1`
  font-size: 22px;
  color: ${colors.gray[900]};
  margin: 0 0 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
`;

const Tab = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  border: 1px solid ${colors.gray[300]};
  background: ${(props) => (props.$active ? colors.gradients.primary : colors.white)};
  color: ${(props) => (props.$active ? colors.white : colors.gray[700])};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
`;

const Filters = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 14px;
  color: ${colors.gray[700]};
`;

const SearchBox = styled.div`
  position: relative;
  min-width: 260px;
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.gray[400]};
  font-size: 13px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 12px 9px 34px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) => props.$bg};
  color: ${(props) => props.$color};
`;

const DeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${colors.error.main};
  background: ${colors.white};
  color: ${colors.error.dark};
  cursor: pointer;
  font-size: 13px;

  &:hover {
    background: ${colors.error.light};
  }
`;

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'em_processamento', label: 'Em processamento' },
  { value: 'concluido', label: 'Concluído' },
  { value: 'falha', label: 'Falha' },
  { value: 'cancelado', label: 'Cancelado' },
];

const TIPO_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  { value: 'atualizacao_geo', label: 'Atualização de geolocalização' },
  { value: 'atualizacao_geo_unidade', label: 'Geolocalização de unidade' },
  { value: 'boas_vindas', label: 'Boas-vindas' },
  { value: 'lembrete_checkin', label: 'Lembrete de checkin' },
  { value: 'notificacao_post', label: 'Notificação de post' },
  { value: 'sync_dados', label: 'Sincronização de dados' },
  { value: 'limpeza_cache', label: 'Limpeza de cache' },
  { value: 'recalculo_score', label: 'Recálculo de score' },
];

const STATUS_BADGE = {
  pendente: { bg: colors.warning.light, color: colors.warning.dark, label: 'Pendente' },
  em_processamento: { bg: colors.info.light, color: colors.info.dark, label: 'Em processamento' },
  concluido: { bg: colors.success.light, color: colors.success.dark, label: 'Concluído' },
  falha: { bg: colors.error.light, color: colors.error.dark, label: 'Falha' },
  cancelado: { bg: colors.gray[100], color: colors.gray[600], label: 'Cancelado' },
};

const REF_LABEL = { usuario: 'Usuário ID', unidade: 'Unidade ID' };

const SEARCH_PLACEHOLDER = {
  usuario: 'Buscar por ID do usuário...',
  unidade: 'Buscar por ID da unidade...',
};

const DEBOUNCE_MS = 400;

function sumField(serie, field) {
  return (serie || []).reduce((acc, point) => acc + (point[field] || 0), 0);
}

export default function Automacoes() {
  // 'score' é tecnicamente origem='space' no backend (recálculo de score é
  // uma automação de space), só que filtrada por tipo_automacao='recalculo_score'.
  const [activeTab, setActiveTab] = useState('usuario');
  const apiOrigem = activeTab === 'score' ? 'space' : activeTab;

  const [dias, setDias] = useState(30);
  const [usuariosSerie, setUsuariosSerie] = useState(null);
  const [spacesSerie, setSpacesSerie] = useState(null);
  const [scoreSerie, setScoreSerie] = useState(null);
  const [unidadesSerie, setUnidadesSerie] = useState(null);

  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFiltro, setStatusFiltro] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const debounceRef = useRef(null);

  useEffect(() => {
    automacoesApi
      .insightsDiario(dias)
      .then((data) => {
        setUsuariosSerie(data.usuarios || []);
        setSpacesSerie(data.spaces || []);
        setScoreSerie(data.score || []);
        setUnidadesSerie(data.unidades || []);
      })
      .catch(() => {
        setUsuariosSerie([]);
        setSpacesSerie([]);
        setScoreSerie([]);
        setUnidadesSerie([]);
      });
  }, [dias]);

  // Ao entrar na aba Score, trava o filtro de tipo em recalculo_score.
  // Ao entrar na aba Spaces, o padrão é atualizacao_geo — sem isso, "Todos os
  // tipos" traria recalculo_score misturado (recalculo_score é do mesmo
  // origem=space no backend, mas deve ficar só na aba Score).
  // Na aba Unidades o tipo é sempre atualizacao_geo_unidade.
  useEffect(() => {
    if (activeTab === 'score') {
      setTipoFiltro('recalculo_score');
    } else if (activeTab === 'unidade') {
      setTipoFiltro('atualizacao_geo_unidade');
    } else if (activeTab === 'space') {
      setTipoFiltro((atual) => (['', 'recalculo_score', 'atualizacao_geo_unidade'].includes(atual) ? 'atualizacao_geo' : atual));
    } else {
      setTipoFiltro((atual) => (['recalculo_score', 'atualizacao_geo_unidade'].includes(atual) ? '' : atual));
    }
  }, [activeTab]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, statusFiltro, tipoFiltro]);

  const load = useCallback(() => {
    setLoading(true);
    automacoesApi
      .list({
        origem: apiOrigem,
        page,
        pageSize: perPage,
        status: statusFiltro || undefined,
        tipoAutomacao: tipoFiltro || undefined,
        search: debouncedSearch || undefined,
      })
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [apiOrigem, page, perPage, statusFiltro, tipoFiltro, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = (row) => {
    const confirmado = window.confirm(
      'Excluir este registro de automação? O usuário/space/unidade voltará a ser processado no próximo ciclo do scheduler.'
    );
    if (!confirmado) return;
    automacoesApi.remove(apiOrigem, row.id).then(load);
  };

  const columns = [
    { key: 'id', label: 'ID Automação' },
    { key: 'ref_id', label: REF_LABEL[activeTab] || 'Space ID', render: (row) => row.usuario_id || row.spaces_multi_id || row.space_id },
    {
      key: 'tipo_automacao',
      label: 'Tipo',
      render: (row) => TIPO_OPTIONS.find((t) => t.value === row.tipo_automacao)?.label || row.tipo_automacao,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => {
        const badge = STATUS_BADGE[row.status] || { bg: colors.gray[100], color: colors.gray[600], label: row.status };
        return <StatusBadge $bg={badge.bg} $color={badge.color}>{badge.label}</StatusBadge>;
      },
    },
    {
      key: 'created_at',
      label: 'Criado em',
      render: (row) => new Date(row.created_at).toLocaleString('pt-BR'),
    },
    {
      key: 'erro',
      label: 'Erro',
      render: (row) => row.dados?.erro || '—',
    },
    {
      key: 'acoes',
      label: 'Ações',
      render: (row) => (
        <DeleteButton onClick={() => handleDelete(row)}>
          <FaTrash /> Excluir
        </DeleteButton>
      ),
    },
  ];

  return (
    <div>
      <Title>Automações</Title>

      <Grid>
        <KpiCard
          icon={FaRobot}
          label="Total no período"
          value={sumField(usuariosSerie, 'total') + sumField(spacesSerie, 'total') + sumField(unidadesSerie, 'total')}
          color={colors.primary[600]}
          bg={colors.primary[100]}
        />
        <KpiCard
          icon={FaCheckCircle}
          label="Sucesso (concluído)"
          value={sumField(usuariosSerie, 'concluido') + sumField(spacesSerie, 'concluido') + sumField(unidadesSerie, 'concluido')}
          color={colors.success.dark}
          bg={colors.success.light}
        />
        <KpiCard
          icon={FaTimesCircle}
          label="Falha"
          value={sumField(usuariosSerie, 'falha') + sumField(spacesSerie, 'falha') + sumField(unidadesSerie, 'falha')}
          color={colors.error.dark}
          bg={colors.error.light}
        />
        <KpiCard
          icon={FaHourglassHalf}
          label="Pendente / processando"
          value={
            sumField(usuariosSerie, 'pendente') +
            sumField(usuariosSerie, 'em_processamento') +
            sumField(spacesSerie, 'pendente') +
            sumField(spacesSerie, 'em_processamento') +
            sumField(unidadesSerie, 'pendente') +
            sumField(unidadesSerie, 'em_processamento')
          }
          color={colors.warning.dark}
          bg={colors.warning.light}
        />
      </Grid>

      <ChartsGrid>
        <DailyChart
          title="Automações de Usuários por dia"
          data={usuariosSerie}
          dias={dias}
          onChangeDias={setDias}
          color={colors.info.main}
        />
        <DailyChart
          title="Automações de Spaces por dia"
          data={spacesSerie}
          dias={dias}
          onChangeDias={setDias}
          color={colors.primary[500]}
        />
        <DailyChart
          title="Geolocalização de Unidades por dia"
          data={unidadesSerie}
          dias={dias}
          onChangeDias={setDias}
          color={colors.warning.main}
        />
        <DailyChart
          title="Recálculo de Score por dia"
          data={scoreSerie}
          dias={dias}
          onChangeDias={setDias}
          color={colors.success.main}
        />
      </ChartsGrid>

      <Toolbar>
        <Tabs>
          <Tab $active={activeTab === 'usuario'} onClick={() => setActiveTab('usuario')}>
            Usuários
          </Tab>
          <Tab $active={activeTab === 'space'} onClick={() => setActiveTab('space')}>
            Spaces
          </Tab>
          <Tab $active={activeTab === 'unidade'} onClick={() => setActiveTab('unidade')}>
            Unidades
          </Tab>
          <Tab $active={activeTab === 'score'} onClick={() => setActiveTab('score')}>
            Score
          </Tab>
        </Tabs>
        <Filters>
          <Select value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)}>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          {activeTab !== 'score' && (
            <Select value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
              {TIPO_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
          <SearchBox>
            <SearchIcon />
            <SearchInput
              placeholder={SEARCH_PLACEHOLDER[activeTab] || 'Buscar por ID do space...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchBox>
        </Filters>
      </Toolbar>

      <DataTable columns={columns} rows={items} loading={loading} />
      {!loading && (
        <Pagination
          page={page}
          perPage={perPage}
          total={total}
          onPageChange={setPage}
          onPerPageChange={(value) => {
            setPerPage(value);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}
